import { ArrowRightOutlined } from '@ant-design/icons';
import { Flex, TableProps } from 'antd';
import Button from 'components/Button';
import { SorterResult } from 'antd/es/table/interface';
import Table from 'components/Table';
import TagComponent from 'components/Tags';
import { TDashboard } from 'hooks/useDashboardQuery';
import { useTranslation } from 'react-i18next';
import { getStatusTagColor } from 'screens/farmer/helper';
import { TaskAssignment } from 'types/common';
import {
  isMobileScreenSize,
  renderDate,
  renderInfoValue,
  tableSortHandler,
} from 'utils/helpers';
import { SelectTaskData } from '../../sales-group-progress';
import TaskStatusList from './TaskStatusList';
import useTrackerWrapper from 'hooks/useTracker';

type DashboardTableProps = {
  data?: TDashboard[] | null;
  total: number;
  loading: boolean;
  currentPage: number;
  pageSize: number;
  sortedInfo: SorterResult<TDashboard>;
  handleChange: TableProps<TDashboard>['onChange'];
  handlePageChange: (page: number, pageSize: number) => void;
  onSelectTask: (data: SelectTaskData) => void;
  onSelectRow: (data: TDashboard) => void;
  selectedRow?: TDashboard;
  isSalesGroup?: boolean;
};

export const getGroupType = (data?: TDashboard) => {
  if (data?.season?.groupType === 'FULL_STACK_GROUP') {
    return 'Full Stack';
  }

  switch (data?.season?.almPractice) {
    case 'NON_ALM':
      return 'Sales Only';
    case 'AWD_1':
    case 'AWD':
      return 'AWD - 1';
    case 'AWD_2':
      return 'AWD - 2';
    default:
      return '-';
  }
};

const DashboardTable = ({
  data,
  total,
  loading = false,
  currentPage,
  pageSize,
  sortedInfo,
  selectedRow,
  handleChange,
  handlePageChange,
  onSelectTask,
  onSelectRow,

  isSalesGroup = false,
}: DashboardTableProps) => {
  const { t } = useTranslation();
  const { onClickWrapper } = useTrackerWrapper();

  const handleClickDetail = (data: TDashboard) => {
    onClickWrapper('Dashboard Detail', () => onSelectRow(data));
  };

  const tableHeader: TableProps<TDashboard>['columns'] = [
    {
      title: t('common.filters.agronomist'),
      dataIndex: 'agronomistName',
      key: 'rizeusersByFieldCoordinator_name',
      sorter: true,
      sortOrder: tableSortHandler(
        sortedInfo,
        'rizeusersByFieldCoordinator_name',
      ),
      className: 'clickable',
      render: (agronomistName) => (
        <Flex align="center" justify="space-between" gap={4}>
          <span>{renderInfoValue(agronomistName)}</span>
          {!isMobileScreenSize ? (
            <Button className="table-click-indicator" type="text">
              <ArrowRightOutlined />
            </Button>
          ) : null}
        </Flex>
      ),
      fixed: 'left',
      width: '15%',
      onCell: (record) => ({
        onClick: () => {
          handleClickDetail(record);
        },
      }),
    },
    {
      title: isSalesGroup
        ? t('salesGroups.salesGroupName')
        : t('dashboard.plantingGroupName'),
      dataIndex: ['season', 'name'],
      key: 'seasons_name',
      sorter: true,
      sortOrder: tableSortHandler(sortedInfo, 'seasons_name'),
      render: (salesGroupName) => (
        <span>{renderInfoValue(salesGroupName)}</span>
      ),
      width: '20%',
    },
    ...(isSalesGroup
      ? []
      : [
          {
            title: t('groupsPage.groupType'),
            dataIndex: 'index',
            key: 'index',
            render: (_: unknown, record: TDashboard) => getGroupType(record),
            width: '10%',
          },
        ]),
    {
      title: t('dashboard.taskStatus'),
      key: 'taskStatus',
      dataIndex: ['season', 'tasks'],
      render: (tasks, data) => (
        <TaskStatusList
          tasks={tasks}
          onClickTask={(task: TaskAssignment) =>
            onSelectTask({ ...data, task, season: data.season })
          }
        />
      ),
      width: '200px',
    },
    {
      title: 'DAP',
      dataIndex: 'dap',
      key: 'dap',
      render: (dap) => <span>{renderInfoValue(dap)}</span>,
      width: '5%',
    },
    {
      title: t('farmerGroupList.seasonStatus'),
      key: 'seasonStatus',
      dataIndex: ['season', 'status'],
      render: (seasonStatus) => (
        <TagComponent
          label={seasonStatus || 'Inactive'}
          color={getStatusTagColor(seasonStatus)}
        />
      ),
      width: '10%',
    },
    {
      title: t('seasonProgressBar.estimatedHarvestDate'),
      dataIndex: ['season', 'estimatedEndDate'],
      key: 'seasons_estimatedEndDate',
      sorter: true,
      sortOrder: tableSortHandler(sortedInfo, 'seasons_estimatedEndDate'),
      render: (estimatedEndDate) => (
        <span>{renderDate(estimatedEndDate, '-', 'DD MMM YYYY')}</span>
      ),
      width: '15%',
    },
    {
      title: isSalesGroup
        ? t('genericForm.District')
        : t('addFgForm.subDistrict'),
      dataIndex: isSalesGroup
        ? ['address', 'district']
        : ['address', 'subDistrict'],
      key: 'district',
      render: (district) => <span>{renderInfoValue(district)}</span>,
      width: '10%',
    },
    {
      title: t('dashboard.participatingPerOwnedHa'),
      key: 'area',
      dataIndex: 'id',
      render: (_, data) => (
        <span>
          {renderInfoValue(data?.participatedArea?.toFixed(2))}/
          {renderInfoValue(data?.ownedArea?.toFixed(2))}
        </span>
      ),
      width: '15%',
    },
  ];

  return (
    <Table
      tableColumns={data ?? []}
      tableHeader={tableHeader}
      loading={loading}
      highlightColor="gray"
      setHighlighted={(record) =>
        record?.season?.id
          ? record?.season?.id === selectedRow?.season?.id
          : false
      }
      onChange={handleChange}
      pagination={{
        total: total,
        current: currentPage,
        pageSize: pageSize,
        onChange: handlePageChange,
        onShowSizeChange: handlePageChange,
        showTotal: (total) => (
          <span style={{ left: 0, position: 'absolute' }}>
            {total} {t('common.totalEntries')}
          </span>
        ),
      }}
    />
  );
};

export default DashboardTable;
